import React, { useState, useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

/**
 * EmailVerificationGuard
 * - ถ้ายังไม่ได้ login → redirect ไป /login
 * - ถ้า login แล้ว status = 'pending' → redirect ไป /email-verification
 * - ถ้า login แล้ว status = 'active' → ผ่านปกติ
 */
const EmailVerificationGuard = ({ children }) => {
  const [status, setStatus] = useState('checking'); // 'checking' | 'active' | 'pending' | 'unauthenticated'
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    const safeSet = (v) => {
      if (mountedRef.current) setStatus(v);
    };

    const checkStatus = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!mountedRef.current) return;
        if (!user) {
          safeSet('unauthenticated');
          return;
        }

        // maybeSingle() — ไม่ throw 406 ถ้าไม่เจอ row
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('status')
          .eq('id', user.id)
          .maybeSingle();
        if (!mountedRef.current) return;

        // user ถูกลบจาก DB แล้ว หรือ profile ไม่พบ → sign out แล้ว redirect
        if (!profile) {
          await supabase.auth.signOut({ scope: 'local' });
          safeSet('unauthenticated');
        } else if (profile.status === 'pending') {
          safeSet('pending');
        } else if (profile.status === 'inactive') {
          await supabase.auth.signOut({ scope: 'local' });
          safeSet('unauthenticated');
        } else {
          safeSet('active');
        }
      } catch (err) {
        if (import.meta.env?.DEV) console.error('EmailVerificationGuard error:', err);
        safeSet('unauthenticated');
      }
    };

    checkStatus();

    return () => {
      mountedRef.current = false;
    };
  }, []);

  if (status === 'checking') return null;
  if (status === 'unauthenticated') return <Navigate to="/login" replace />;
  if (status === 'pending') return <Navigate to="/email-verification" replace />;

  return children;
};

export default EmailVerificationGuard;
